import type { ShipmentTableState } from 'src/app/models/table-params.model';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import { ToastProps } from 'src/app/models/toast.model';
import { PlantationsBySi } from 'src/app/models/traceability-state.model';

@Injectable({
  providedIn: 'root'
})
export class EventStateService {
  private readonly _isCreateNewSiloading$ = new BehaviorSubject<boolean>(false);
  private readonly _isNewSiCreated$ = new Subject<boolean>();
  private readonly _toast$ = new Subject<ToastProps>();
  // for SI table risks
  private readonly _isRiskLoading$ = new BehaviorSubject<boolean>(false);
  private readonly _isPlantationGeometryLoading$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _error$ = new BehaviorSubject<HttpErrorResponse | null>(
    null
  );
  private readonly _hasError$ = new BehaviorSubject<boolean>(false);
  private readonly _isPlantationBySiLoaded$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _isSiSummariesLoaded$ = new BehaviorSubject<boolean>(false);
  private readonly _isSourcingAreaLoaded$ = new BehaviorSubject<boolean>(false);
  private readonly _downloadEuisLoading$ = new BehaviorSubject<boolean>(false);
  private readonly _downloadCombinedEuisLoading$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _isTraceabilityTableLoading$ = new BehaviorSubject<boolean>(
    true
  );
  private readonly _downloadFullRiskLoading$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _downloadEudrPackageLoading$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _downloadGpsnrDdrLoading$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _downloadAssessmentReportLoading$ =
    new BehaviorSubject<boolean>(false);
  private readonly _isLinkPlantationLoading$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _isPlantationLinked$ = new Subject<boolean>();
  private readonly _isDownloadDdrLoading$ = new BehaviorSubject<boolean>(false);
  private readonly _isVerifyingSecretKeyLoading$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _isDownloadingExternalJsonFileLoading$ =
    new BehaviorSubject<boolean>(false);
  private readonly _isDownloadingExternalZipFileLoading$ =
    new BehaviorSubject<boolean>(false);
  private readonly _isSettingsLoaded$ = new BehaviorSubject<boolean>(false);
  private readonly _isDdrDownloaded$ = new Subject<boolean>();
  private readonly _isDdrLoading$ = new BehaviorSubject<boolean>(false);
  private readonly _zoomPlantation$ = new Subject<PlantationsBySi[]>();
  private readonly _traceabilityTableParams$ =
    new BehaviorSubject<ShipmentTableState | null>(null);
  private readonly _hoveredPlantation$ = new Subject<PlantationsBySi | null>();
  private readonly _isPlantationFilterLoading$ = new BehaviorSubject<boolean>(
    false
  );

  private readonly _isDashboardTableLoading$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _isTraderLinkingTRN$ = new BehaviorSubject<boolean>(false);
  private readonly _isTraderCopyingInProgress$ = new BehaviorSubject<boolean>(
    false
  );
  // used in map page selected plantation details section
  private readonly _isShowMapPlantationDetails$ = new BehaviorSubject<boolean>(
    false
  );
  // for lazy loading risks column in traceability table
  private readonly _isRisksColumnLoading$ = new BehaviorSubject<boolean>(false);
  // Failed section loads show an inline error instead of data.
  private readonly _isRisksColumnFailed$ = new BehaviorSubject<boolean>(false);
  private readonly _isPlantationBySiFailed$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _isSourcingAreaFailed$ = new BehaviorSubject<boolean>(false);

  get isCreatedNewSiLoading$() {
    return this._isCreateNewSiloading$.asObservable();
  }

  set isCreatedNewSiLoading(isLoading: boolean) {
    this._isCreateNewSiloading$.next(isLoading);
  }

  get isVerifyingSecretKeyLoading$() {
    return this._isVerifyingSecretKeyLoading$.asObservable();
  }

  set isVerifyingSecretKeyLoading(isLoading: boolean) {
    this._isVerifyingSecretKeyLoading$.next(isLoading);
  }

  get isDownloadingExternalJsonFileLoading$() {
    return this._isDownloadingExternalJsonFileLoading$.asObservable();
  }

  set isDownloadingExternalJsonFileLoading(isLoading: boolean) {
    this._isDownloadingExternalJsonFileLoading$.next(isLoading);
  }

  get isDownloadingExternalZipFileLoading$() {
    return this._isDownloadingExternalZipFileLoading$.asObservable();
  }

  set isDownloadingExternalZipFileLoading(isLoading: boolean) {
    this._isDownloadingExternalZipFileLoading$.next(isLoading);
  }

  get isPlantationGeometryLoading$() {
    return this._isPlantationGeometryLoading$.asObservable();
  }

  set isPlantationGeometryLoading(isLoading: boolean) {
    this._isPlantationGeometryLoading$.next(isLoading);
  }

  get isLinkPlantationLoading$() {
    return this._isLinkPlantationLoading$.asObservable();
  }

  set isLinkPlantationLoading(isLoading: boolean) {
    this._isLinkPlantationLoading$.next(isLoading);
  }

  get isSettingsLoaded$() {
    return this._isSettingsLoaded$.asObservable();
  }

  set isSettingsLoaded(isLoading: boolean) {
    this._isSettingsLoaded$.next(isLoading);
  }

  get isPlantationLinked$() {
    return this._isPlantationLinked$.asObservable();
  }

  set isPlantationLinked(isLinked: boolean) {
    this._isPlantationLinked$.next(isLinked);
  }

  get isDownloadDdrLoading$() {
    return this._isDownloadDdrLoading$.asObservable();
  }

  set isDownloadDdrLoading(isLoading: boolean) {
    this._isDownloadDdrLoading$.next(isLoading);
  }

  get isDdrDownloaded$() {
    return this._isDdrDownloaded$.asObservable();
  }

  set isDdrDownloaded(isLinked: boolean) {
    this._isDdrDownloaded$.next(isLinked);
  }

  get isDdrLoading$() {
    return this._isDdrLoading$.asObservable();
  }

  set isDdrLoading(_isDdrLoading: boolean) {
    this._isDdrLoading$.next(_isDdrLoading);
  }

  get zoomPlantation$() {
    return this._zoomPlantation$.asObservable();
  }

  set zoomPlantation(plantation: PlantationsBySi[]) {
    this._zoomPlantation$.next(plantation);
  }

  get traceabilityTableParams$() {
    return this._traceabilityTableParams$.asObservable();
  }

  set traceabilityTableParams(filters: ShipmentTableState | null) {
    this._traceabilityTableParams$.next(filters);
  }

  get isNewSiCreated$() {
    return this._isNewSiCreated$.asObservable();
  }

  set isNewSiCreated(isCreated: boolean) {
    this._isNewSiCreated$.next(isCreated);
  }

  get toast$() {
    return this._toast$.asObservable();
  }

  set toast(toast: ToastProps) {
    this._toast$.next(toast);
  }

  get isRiskLoading$() {
    return this._isRiskLoading$.asObservable();
  }

  set isRiskLoading(isRiskLoading: boolean) {
    this._isRiskLoading$.next(isRiskLoading);
  }

  get isSiSummariesLoaded$() {
    return this._isSiSummariesLoaded$.asObservable();
  }

  set isSiSummariesLoaded(isSiSummariesLoaded: boolean) {
    this._isSiSummariesLoaded$.next(isSiSummariesLoaded);
  }

  get isPlantationBySiLoaded$() {
    return this._isPlantationBySiLoaded$.asObservable();
  }

  set isPlantationBySiLoaded(isPlantationBySiLoaded: boolean) {
    this._isPlantationBySiLoaded$.next(isPlantationBySiLoaded);
  }

  get isSourcingAreaLoaded$() {
    return this._isSourcingAreaLoaded$.asObservable();
  }

  set isSourcingAreaLoaded(isSourcingAreaLoaded: boolean) {
    this._isSourcingAreaLoaded$.next(isSourcingAreaLoaded);
  }

  get error$() {
    return this._error$.asObservable();
  }

  set error(error: HttpErrorResponse | null) {
    this._error$.next(error);
  }

  get downloadEuisLoading$() {
    return this._downloadEuisLoading$.asObservable();
  }

  set downloadEuisLoading(downloadEuisLoading: boolean) {
    this._downloadEuisLoading$.next(downloadEuisLoading);
  }

  get downloadCombinedEuisLoading$() {
    return this._downloadCombinedEuisLoading$.asObservable();
  }

  set downloadCombinedEuisLoading(downloadEuisLoading: boolean) {
    this._downloadCombinedEuisLoading$.next(downloadEuisLoading);
  }

  get downloadFullRiskLoading$() {
    return this._downloadFullRiskLoading$.asObservable();
  }

  set downloadFullRiskLoading(downloadFullRiskLoading: boolean) {
    this._downloadFullRiskLoading$.next(downloadFullRiskLoading);
  }

  get downloadEudrPackageLoading$() {
    return this._downloadEudrPackageLoading$.asObservable();
  }

  set downloadEudrPackageLoading(downloadEudrPackageLoading: boolean) {
    this._downloadEudrPackageLoading$.next(downloadEudrPackageLoading);
  }

  get downloadGpsnrDdrLoading$() {
    return this._downloadGpsnrDdrLoading$.asObservable();
  }

  set downloadGpsnrDdrLoading(downloadGpsnrDdrLoading: boolean) {
    this._downloadGpsnrDdrLoading$.next(downloadGpsnrDdrLoading);
  }

  get downloadAssessmentReportLoading$() {
    return this._downloadAssessmentReportLoading$.asObservable();
  }

  set downloadAssessmentReportLoading(
    downloadAssessmentReportLoading: boolean
  ) {
    this._downloadAssessmentReportLoading$.next(
      downloadAssessmentReportLoading
    );
  }

  get hoveredPlantation$() {
    return this._hoveredPlantation$.asObservable();
  }

  set hoveredPlantation(hoveredPlantation: PlantationsBySi | null) {
    this._hoveredPlantation$.next(hoveredPlantation);
  }

  get isTraceabilityTableLoading$() {
    return this._isTraceabilityTableLoading$.asObservable();
  }

  set isTraceabilityTableLoading(isTraceabilityTableLoading: boolean) {
    this._isTraceabilityTableLoading$.next(isTraceabilityTableLoading);
  }

  get isPlantationFilterLoading$() {
    return this._isPlantationFilterLoading$.asObservable();
  }

  set isPlantationFilterLoading(isPlantationFilterLoading: boolean) {
    this._isPlantationFilterLoading$.next(isPlantationFilterLoading);
  }

  get isDashboardTableLoading$() {
    return this._isDashboardTableLoading$.asObservable();
  }

  set isDashboardTableLoading(isDashboardTableLoading: boolean) {
    this._isDashboardTableLoading$.next(isDashboardTableLoading);
  }

  get hasError$() {
    return this._hasError$.asObservable();
  }

  set hasError(hasError: boolean) {
    this._hasError$.next(hasError);
  }

  // TODO: remove this
  get isTraderLinkingTRN$() {
    return this._isTraderLinkingTRN$.asObservable();
  }

  // TODO: remove this
  set isTraderLinkingTRN(isTraderLinkingTRN: boolean) {
    this._isTraderLinkingTRN$.next(isTraderLinkingTRN);
  }

  get isTraderCopyingInProgress$() {
    return this._isTraderCopyingInProgress$.asObservable();
  }

  set isTraderCopyingInProgress(isTraderLinkingTRN: boolean) {
    this._isTraderCopyingInProgress$.next(isTraderLinkingTRN);
  }

  get isShowMapPlantationDetails$() {
    return this._isShowMapPlantationDetails$.asObservable();
  }

  set isShowMapPlantationDetails(isShowMapPlantationDetails: boolean) {
    this._isShowMapPlantationDetails$.next(isShowMapPlantationDetails);
  }

  get isRisksColumnLoading$() {
    return this._isRisksColumnLoading$.asObservable();
  }

  set isRisksColumnLoading(loading: boolean) {
    this._isRisksColumnLoading$.next(loading);
  }

  get isRisksColumnFailed$() {
    return this._isRisksColumnFailed$.asObservable();
  }

  set isRisksColumnFailed(failed: boolean) {
    this._isRisksColumnFailed$.next(failed);
  }

  get isPlantationBySiFailed$() {
    return this._isPlantationBySiFailed$.asObservable();
  }

  set isPlantationBySiFailed(failed: boolean) {
    this._isPlantationBySiFailed$.next(failed);
  }

  get isSourcingAreaFailed$() {
    return this._isSourcingAreaFailed$.asObservable();
  }

  set isSourcingAreaFailed(failed: boolean) {
    this._isSourcingAreaFailed$.next(failed);
  }

  resetAllLoadingStates() {
    this.downloadEuisLoading = false;
    this.downloadFullRiskLoading = false;
    this.isRiskLoading = false;
    this.isCreatedNewSiLoading = false;
    this.downloadEudrPackageLoading = false;
    this.isPlantationGeometryLoading = false;
    this.isDownloadDdrLoading = false;
    this.downloadGpsnrDdrLoading = false;
  }

  constructor() {}
}
